import React, { useEffect, useMemo, useState } from "react";
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BusinessIcon from '@mui/icons-material/Business';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CircularProgress from '@mui/material/CircularProgress';
import DeleteIcon from '@mui/icons-material/Delete';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import IconButton from '@mui/material/IconButton';
import { FieldInput } from '../../ui/FieldInput';
import { AppPagination } from '../../ui/AppPagination';
import { paginate } from '../../../utils/paginate';

type ApiDepartmentRead = {
  id: number;
  name: string;
  subfolder_name: string;
};

type ApiDepartmentListResponse = {
  total: number;
  page: number;
  size: number;
  items: ApiDepartmentRead[];
};

export type DepartmentEmail = {
  id: number;
  department_id: number;
  email: string;
  label: string | null;
  is_active: boolean;
  created_at?: string;
};

export type Department = {
  id: string;
  name: string;
  subfolderName: string;
};

type DepartmentForm = {
  name: string;
  subfolderName: string;
};

type Mode = "list" | "edit" | "create";

type Props = {
  onLoad?: (departments: Department[]) => void;
  onSelect?: (department: Department | null) => void;
  initialSelectedId?: string | null;
  refreshTrigger?: number;
  onRefresh?: () => void;
  onLogout?: () => void;
};

const PAGE_SIZE = 8;

// Получаем базовый URL из env и формируем URL для запросов к отделам
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
const DEPARTMENTS_URL = `${API_BASE_URL}/api/v1/departments/`;

const defaultForm: DepartmentForm = {
  name: "",
  subfolderName: "",
};

const mapApiToUi = (d: ApiDepartmentRead): Department => ({
  id: String(d.id),
  name: d.name,
  subfolderName: d.subfolder_name,
});

const DepartmentListItem: React.FC<{ dep: Department; onClick: () => void; isSelected: boolean }> = ({ dep, onClick, isSelected }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left rounded-lg border p-4 shadow-sm transition-all focus:outline-none ${isSelected ? 'bg-blue-50 border-blue-400 shadow-md' : 'bg-white border-gray-200 hover:border-blue-400 hover:shadow-md'}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BusinessIcon className={isSelected ? 'text-[#1E61AF]' : 'text-gray-400'} />
          <div className="flex flex-col">
            <span className={`font-medium ${isSelected ? 'text-[#1E61AF]' : 'text-gray-800'}`}>{dep.name}</span>
            <span className="text-xs text-gray-500">Папка: {dep.subfolderName}</span>
          </div>
        </div>
        <ChevronRightIcon className={isSelected ? 'text-[#1E61AF]' : 'text-gray-400'} />
      </div>
    </button>
  );
};

export const DepartmentManager: React.FC<Props> = ({ onLoad, onSelect, initialSelectedId, refreshTrigger, onRefresh, onLogout }) => {
  const [mode, setMode] = useState<Mode>("list");
  const [search, setSearch] = useState("");
  const [deps, setDeps] = useState<Department[]>([]);
  const [selected, setSelected] = useState<Department | null>(null);

  const [form, setForm] = useState<DepartmentForm>(defaultForm);
  const [errors, setErrors] = useState<Partial<Record<keyof DepartmentForm, string>>>({});

  const [emails, setEmails] = useState<DepartmentEmail[]>([]);
  const [newEmail, setNewEmail] = useState({ email: "", label: "" });
  const [emailLoading, setEmailLoading] = useState(false);

  const [flash, setFlash] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string>("");

  const getAuthHeaders = (): HeadersInit => {
    const token = localStorage.getItem('token');
    return {
      "Accept": "application/json",
      "Content-Type": "application/json",
      ...(token ? { "Authorization": token } : {})
    };
  };

  const handleUnauthorized = (status: number) => {
    if (status === 401 && onLogout) {
      onLogout();
      return true;
    }
    return false;
  };

  const showFlash = (msg: string) => {
    setFlash(msg);
    setTimeout(() => setFlash(""), 3000);
  };

  useEffect(() => {
    const fetchDepartments = async () => {
      setLoading(true);
      setLoadError("");
      try {
        const res = await fetch(DEPARTMENTS_URL, { headers: getAuthHeaders() });

        if (handleUnauthorized(res.status)) return;
        if (!res.ok) throw new Error(`Ошибка загрузки: ${res.status}`);

        const data: ApiDepartmentListResponse = await res.json();
        const mapped = (data.items || []).map(mapApiToUi);
        setDeps(mapped);
        onLoad?.(mapped);

        if (initialSelectedId) {
            const depToSelect = mapped.find(o => o.id === initialSelectedId);
            if (depToSelect) {
                openEdit(depToSelect);
            }
        }
      } catch (e: any) {
        setLoadError(e?.message || "Не удалось загрузить отделы");
      } finally {
        setLoading(false);
      }
    };
    fetchDepartments();
  }, [onLoad, initialSelectedId, onSelect, refreshTrigger]);

  const loadEmails = async (departmentId: string) => {
    setEmailLoading(true);
    try {
      const res = await fetch(`${DEPARTMENTS_URL}${departmentId}/emails/`, { headers: getAuthHeaders() });
      if (handleUnauthorized(res.status)) return;
      if (res.ok) {
        const data = await res.json();
        setEmails(data);
      }
    } catch (e) {
      console.error("Не удалось загрузить email-адреса", e);
    } finally {
      setEmailLoading(false);
    }
  };

  const handleAddEmail = async () => {
    if (!newEmail.email.trim()) return;
    try {
      const res = await fetch(`${DEPARTMENTS_URL}${selected!.id}/emails/`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          email: newEmail.email.trim(),
          label: newEmail.label.trim() || null,
          is_active: true
        })
      });
      if (handleUnauthorized(res.status)) return;
      if (res.ok) {
        setNewEmail({ email: "", label: "" });
        loadEmails(selected!.id);
        showFlash("Email успешно добавлен");
      } else {
        const err = await res.json();
        showFlash(`Ошибка добавления email: ${err?.detail || res.status}`);
      }
    } catch (e: any) {
      showFlash("Не удалось добавить email");
    }
  };

  const handleToggleEmail = async (email: DepartmentEmail) => {
    try {
      const res = await fetch(`${DEPARTMENTS_URL}${selected!.id}/emails/${email.id}`, {
        method: "PATCH",
        headers: getAuthHeaders(),
        body: JSON.stringify({ is_active: !email.is_active })
      });
      if (handleUnauthorized(res.status)) return;
      if (res.ok) {
        loadEmails(selected!.id);
      }
    } catch (e) {
      showFlash("Не удалось изменить статус email");
    }
  };

  const handleDeleteEmail = async (emailId: number) => {
    if (!window.confirm("Удалить этот email?")) return;
    try {
      const res = await fetch(`${DEPARTMENTS_URL}${selected!.id}/emails/${emailId}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });
      if (handleUnauthorized(res.status)) return;
      if (res.ok) {
        loadEmails(selected!.id);
        showFlash("Email удален");
      }
    } catch (e) {
      showFlash("Не удалось удалить email");
    }
  };


  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return deps;
    return deps.filter((d) => [d.name, d.subfolderName].filter(Boolean).join(" ").toLowerCase().includes(q));
  }, [search, deps]);

  useEffect(() => { setPage(1); }, [search]);

  const { items: paged, totalItems, totalPages } = useMemo(() => paginate(filtered, page, PAGE_SIZE), [filtered, page]);

  useEffect(() => { if (totalPages > 0 && page > totalPages) setPage(totalPages); }, [page, totalPages]);

  const openEdit = (dep: Department) => {
    setSelected(dep);
    setForm({ name: dep.name, subfolderName: dep.subfolderName });
    setMode("edit");
    setErrors({});
    setNewEmail({ email: "", label: "" });
    onSelect?.(dep);
    loadEmails(dep.id);
  };

  const openCreate = () => {
    setSelected(null);
    setForm(defaultForm);
    setErrors({});
    setEmails([]);
    setMode("create");
    onSelect?.(null);
  };

  const closePanel = () => {
    setMode("list");
  };

  const validate = (v: DepartmentForm) => {
    const e: Partial<Record<keyof DepartmentForm, string>> = {};
    if (!v.name.trim()) e.name = "Укажите название отдела";
    if (!v.subfolderName.trim()) e.subfolderName = "Укажите название папки";
    return e;
  };

  const handleFormChange = (field: keyof DepartmentForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => { const n = { ...prev }; delete n[field]; return n; });
    }
  };

  const onSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const eMap = validate(form);
    setErrors(eMap);
    if (Object.keys(eMap).length > 0) return;

    const isEdit = mode === 'edit' && selected;

    const body = isEdit
        ? { name: form.name.trim(), subfolder_name: form.subfolderName.trim(), id: Number(selected!.id) }
        : { name: form.name.trim(), subfolder_name: form.subfolderName.trim() };

    const url = isEdit ? `${DEPARTMENTS_URL}${selected!.id}` : DEPARTMENTS_URL;
    const method = isEdit ? "PUT" : "POST";

    try {
      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });

      if (handleUnauthorized(res.status)) return;

      if (!res.ok) {
        let message = `Ошибка сохранения (${res.status})`;
        try { const errBody = await res.json(); message = errBody?.detail || message; } catch {}
        throw new Error(message);
      }

      showFlash(isEdit ? "Отдел успешно обновлен" : "Отдел успешно создан");

      if (!isEdit) setMode("list");
      onRefresh?.();
    } catch (err: any) {
      showFlash(err?.message || "Не удалось сохранить отдел");
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    if (!window.confirm(`Вы уверены, что хотите удалить отдел "${selected.name}"? Программы и email-адреса, связанные с ним, будут отвязаны или удалены.`)) return;

    try {
        const res = await fetch(`${DEPARTMENTS_URL}${selected.id}`, {
          method: 'DELETE',
          headers: getAuthHeaders(),
        });

        if (handleUnauthorized(res.status)) return;

        if (!res.ok) {
            let message = `Ошибка удаления (${res.status})`;
            try { const errBody = await res.json(); message = errBody?.detail || message; } catch {}
            throw new Error(message);
        }
        showFlash("Отдел успешно удален");
        setMode("list");
        onRefresh?.();
    } catch (err: any) {
        showFlash(err?.message || "Не удалось удалить отдел");
    }
  };

  const isPanelOpen = mode === 'edit' || mode === 'create';

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
      <header className="flex items-center justify-between gap-4 p-4 sm:p-5 border-b border-gray-200">
        <h1 className="text-lg font-medium text-gray-900">Отделы</h1>
        <button
          type="button"
          onClick={mode === 'create' ? closePanel : openCreate}
          className={`inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors focus:outline-none ${mode === 'create' ? 'bg-gray-600 hover:bg-gray-700' : 'bg-[#1E61AF] hover:bg-blue-700'}`}
        >
          {mode === 'create' ? <ArrowBackIcon fontSize="small" /> : <AddIcon fontSize="small" />}
          {mode === 'create' ? 'К списку' : 'Создать отдел'}
        </button>
      </header>

      <div className="p-4 sm:p-6">
        {flash && (
          <div className={`mb-4 flex items-center gap-3 text-sm rounded-lg px-4 py-3 border ${flash.includes("Ошибка") || flash.includes("Не удалось") ? 'bg-red-50 text-red-800 border-red-200' : 'bg-green-100 text-green-800 border-green-200'}`}>
            <CheckCircleOutlineIcon className={flash.includes("Ошибка") ? "text-red-600" : "text-green-600"} />
            {flash}
          </div>
        )}

        <div className={`grid grid-cols-1 gap-8 transition-all duration-300 ease-in-out ${isPanelOpen ? 'lg:grid-cols-2' : 'lg:grid-cols-1'}`}>

          {/* Левая панель - Список */}
          <div className={`space-y-4 ${mode === 'create' ? 'hidden lg:block' : ''} ${isPanelOpen ? '' : 'lg:col-span-2'}`}>
            <div className="relative">
                <FieldInput
                    label=""
                    placeholder="Поиск по названию или папке..."
                    value={search}
                    onChange={setSearch}
                    icon={<SearchIcon fontSize="small" className="text-gray-400" />}
                />
                {search && (
                    <button type="button" onClick={() => setSearch("")} className="absolute top-[13px] right-3 flex items-center">
                        <ClearIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                    </button>
                )}
            </div>

            <div className="space-y-3">
              {loading ? ( <div className="flex justify-center p-12"><CircularProgress /></div> )
              : loadError ? ( <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">{loadError}</div> )
              : totalItems === 0 ? (
                <div className="rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
                  <h3 className="text-lg font-medium text-gray-500">{search ? "Ничего не найдено" : "Отделы отсутствуют"}</h3>
                  <p className="mt-1 text-sm text-gray-500">{search ? "Попробуйте изменить запрос." : "Нажмите 'Создать', чтобы добавить первый."}</p>
                </div>
              ) : (
                <>
                  {paged.map((dep: Department) => ( <DepartmentListItem key={dep.id} dep={dep} onClick={() => openEdit(dep)} isSelected={!!selected && selected.id === dep.id} /> ))}
                  {totalPages > 1 && <AppPagination page={page} totalItems={totalItems} pageSize={PAGE_SIZE} onChange={setPage} className="pt-2" />}
                </>
              )}
            </div>
          </div>

          {/* Правая панель - Форма */}
          {isPanelOpen && (
            <div className="rounded-lg border border-gray-200 bg-white shadow-lg h-fit flex flex-col">
               <div className="flex items-center justify-between p-4 sm:p-6 border-b">
                  <div className="flex items-center gap-3">
                    <button type="button" onClick={closePanel} className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 lg:hidden">
                      <ArrowBackIcon />
                    </button>
                    <h2 className="text-lg font-semibold text-gray-900">
                      {mode === 'edit' ? 'Редактирование отдела' : 'Создание отдела'}
                    </h2>
                  </div>
                </div>

                <div className="p-4 sm:p-6 space-y-6 overflow-y-auto max-h-[calc(100vh-250px)]">
                    <form id="department-form" onSubmit={onSave} noValidate className="space-y-4">
                        <FieldInput
                        label="Название отдела"
                        value={form.name}
                        onChange={(v: string) => handleFormChange('name', v)}
                        error={errors.name} required
                        />
                        <FieldInput
                        label="Название папки для отчетов"
                        value={form.subfolderName}
                        onChange={(v: string) => handleFormChange('subfolderName', v)}
                        error={errors.subfolderName} required
                        />
                    </form>

                    {/* Блок управления Email-адресами доступен только при редактировании */}
                    {mode === 'edit' && (
                        <div className="border-t pt-6 mt-6">
                            <h3 className="text-md font-medium text-gray-900 mb-4 flex items-center gap-2">
                                <MailOutlineIcon className="text-gray-500" fontSize="small"/>
                                Email адреса для уведомлений
                            </h3>

                            <div className="space-y-3 mb-5">
                                {emailLoading ? (
                                    <div className="flex justify-center py-4"><CircularProgress size={24}/></div>
                                ) : emails.length === 0 ? (
                                    <p className="text-sm text-gray-500 italic">Нет привязанных email-адресов.</p>
                                ) : (
                                    emails.map(email => (
                                        <div key={email.id} className="flex justify-between items-center p-3 border rounded-md bg-gray-50 shadow-sm">
                                            <div className="flex flex-col overflow-hidden">
                                                <span className={`font-medium text-sm truncate ${email.is_active ? 'text-gray-900' : 'text-gray-400 line-through'}`}>
                                                    {email.email}
                                                </span>
                                                {email.label && <span className="text-xs text-gray-500 truncate">{email.label}</span>}
                                            </div>
                                            <div className="flex items-center gap-3 shrink-0 pl-2">
                                                <label className="flex items-center cursor-pointer gap-2 text-xs text-gray-600">
                                                    <input
                                                        type="checkbox"
                                                        checked={email.is_active}
                                                        onChange={() => handleToggleEmail(email)}
                                                        className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                                                    />
                                                    Активен
                                                </label>
                                                <IconButton size="small" color="error" onClick={() => handleDeleteEmail(email.id)}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>

                            {/* Форма добавления Email */}
                            <div className="bg-blue-50/50 p-4 rounded-lg border border-blue-100">
                                <h4 className="text-sm font-medium text-gray-700 mb-3">Добавить новый Email</h4>
                                <div className="flex flex-col sm:flex-row gap-3 items-start">
                                    <div className="w-full sm:flex-1">
                                        <FieldInput
                                            label="Email адрес"
                                            value={newEmail.email}
                                            onChange={(v) => setNewEmail(p => ({ ...p, email: v }))}
                                            placeholder="example@mail.com"
                                        />
                                    </div>
                                    <div className="w-full sm:flex-1">
                                        <FieldInput
                                            label="Описание (опционально)"
                                            value={newEmail.label}
                                            onChange={(v) => setNewEmail(p => ({ ...p, label: v }))}
                                            placeholder="Напр. Директор"
                                        />
                                    </div>
                                </div>
                                <div className="mt-3 flex justify-end">
                                    <button
                                        type="button"
                                        onClick={handleAddEmail}
                                        disabled={!newEmail.email.trim()}
                                        className="rounded-md bg-white border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none disabled:opacity-50 transition-colors"
                                    >
                                        Добавить Email
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Кнопки управления отделом выровнены по правой стороне */}
                <div className="flex justify-end gap-3 p-4 bg-gray-50 rounded-b-lg border-t mt-auto">
                    {mode === 'edit' && (
                        <button
                            type="button"
                            onClick={handleDelete}
                            className="inline-flex items-center justify-center gap-2 rounded-md bg-white border border-red-200 px-4 py-2 text-sm font-semibold text-red-600 shadow-sm hover:bg-red-50 transition-colors"
                        >
                            <DeleteIcon fontSize="small"/>
                            Удалить отдел
                        </button>
                    )}
                    <button
                        type="submit"
                        form="department-form"
                        className="inline-flex items-center justify-center gap-2 rounded-md bg-[#1E61AF] px-6 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 transition-colors"
                    >
                        {mode === 'edit' ? 'Сохранить изменения' : 'Создать отдел'}
                    </button>
                </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};